import { ChangeDetectionStrategy, Component, effect, input, signal, untracked } from "@angular/core";
import { EvLogoLoaderComponent } from "src/app/shared-components/ev-logo-loader/ev-logo-loader.component";

/**
 * Centred loading indicator for the map canvas. Sits on top of the map without
 * blocking it — the previous markers stay visible and interactive underneath while
 * new data is fetched.
 *
 * Map panning re-triggers the data fetch continuously, so the raw loading flag is
 * far too twitchy to bind to directly. This component only shows the loader once
 * loading has lasted `showAfterMs`, and once shown keeps it up for `minVisibleMs`,
 * which turns a stream of on/off flickers into either nothing at all or one calm
 * appearance.
 *
 * @example
 * ```html
 * <map-loading-overlay [loading]="mapDataIsLoading()" />
 * ```
 */
@Component({
    selector: "map-loading-overlay",
    imports: [EvLogoLoaderComponent],
    templateUrl: "./map-loading-overlay.component.html",
    styleUrl: "./map-loading-overlay.component.scss",
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class MapLoadingOverlayComponent {
    /** The raw loading flag. Bind straight to a resource's `isLoading` — the debouncing happens here. */
    readonly loading = input.required<boolean>();

    /** How long loading must last before the loader appears. Shorter fetches show nothing at all. */
    readonly showAfterMs = input<number>(250);

    /** How long the loader stays up once shown, so a fast response cannot make it blink. */
    readonly minVisibleMs = input<number>(500);

    protected readonly visible = signal(false);

    private shownAt = 0;

    constructor() {
        effect((onCleanup) => {
            const loading = this.loading();
            const showAfter = this.showAfterMs();
            const minVisible = this.minVisibleMs();
            let timer: ReturnType<typeof setTimeout> | null = null;

            // `visible` is read untracked on purpose: this effect writes it, and tracking it
            // would make the effect retrigger itself.
            if (loading && !untracked(this.visible)) {
                timer = setTimeout(() => {
                    this.shownAt = Date.now();
                    this.visible.set(true);
                }, showAfter);
            } else if (!loading && untracked(this.visible)) {
                const remaining = minVisible - (Date.now() - this.shownAt);

                if (remaining <= 0) {
                    this.visible.set(false);
                } else {
                    timer = setTimeout(() => this.visible.set(false), remaining);
                }
            }

            // Runs before the next execution and on destroy, so a pending show is cancelled
            // when loading stops early, and a pending hide is cancelled when it starts again.
            onCleanup(() => {
                if (timer) clearTimeout(timer);
            });
        });
    }
}
